import styled, { css } from 'styled-components';
import { TextInput } from 'src/components/inputs';
import { layout, media } from '@prisjakt/ui-base';
import {
  PrimaryButton,
  ButtonText as ButtonTextComponent,
  ButtonIcon as ButtonIconComponent,
} from '@prisjakt/ui/button/variants';
import CheckCircleIcon from 'mdi-react/CheckCircleIcon';
import { Variant } from './types';
import {
  BodyMediumText,
  BodySmallText,
  H3Text,
} from '@prisjakt/ui/text/variants';

export const Legal = styled.div`
  font-size: ${layout.measurements.fontSizeSmall};
  text-decoration: underline;

  ${media.mediumDown`
    font-size: ${layout.measurements.fontSizeSmall};
    order: 9;
    align-self: flex-start;
  `}
`;

export const Feedback = styled.div`
  font-size: ${layout.measurements.fontSizeSmall};
`;

// Empty SC component to allow for it to be CSS selected
export const Title = styled(H3Text)``;

export const SubscribeTitle = styled.b`
  margin-left: ${layout.measurements.spacing050};
`;
export const SubscribeSubTitle = styled.div`
  margin-bottom: ${layout.measurements.spacing050};
  margin-top: ${layout.measurements.spacing050};
`;

export const SubscribedCheckIcon = styled(CheckCircleIcon).attrs(() => ({
  size: '16px',
}))`
  border-radius: 16px;
  background-color: white;
  fill: ${p => p.theme.colors.positive500};
  vertical-align: middle;
`;

export const Form = styled.form`
  margin-bottom: ${layout.measurements.spacing050};
  margin-top: ${layout.measurements.spacing100};
  flex-grow: 1;
`;

type InlineInputProps = { stacked?: boolean };
export const InlineInput = styled(TextInput)<InlineInputProps>`
  flex-grow: 1;

  > input {
    height: 100%;
  }

  ${p =>
    !p.stacked &&
    css`
      margin-right: 20px;
      margin-top: 2px;
    `}
`;

export const Flex = styled.div`
  display: flex;
  align-items: top;
  flex-direction: 'row';
  background-color: 'red';
  flex-grow: 1;
`;

export const Text = styled(BodySmallText)`
  margin-bottom: ${layout.measurements.spacing100};
`;

export const Button = styled(PrimaryButton)`
  margin-left: ${layout.measurements.spacing050};
  height: min-content;
  width: 100%;
`;

export const ButtonText = styled(ButtonTextComponent)<{ $hide: boolean }>`
  visibility: ${p => (p.$hide ? 'hidden' : 'visible')};
`;

export const ButtonIcon = styled(ButtonIconComponent)`
  position: absolute;
  left: 50%;
  transform: translate(-50%, 0);
`;

type NewsLetterProps = { variant: Variant } & { stacked: boolean };
export const NewsLetter = styled.div<NewsLetterProps>`
  margin: ${layout.measurements.spacing050};
  color: ${p => p.variant === 'secondary' && p.theme.colors.base600};
  ${Title} {
    color: ${p => p.variant === 'secondary' && p.theme.colors.base700};
  }
  ${Legal} a {
    color: ${p =>
      p.variant === 'primary'
        ? p.theme.colors.base600
        : p.theme.colors.base600};

    :hover {
      color: ${p =>
        p.variant === 'primary'
          ? p.theme.colors.base600
          : p.theme.text.callToActionHover};
    }
  }
  ${Feedback} {
    color: ${p =>
      p.variant === 'primary'
        ? p.theme.colors.negative700
        : p.theme.colors.base600};
  }
  ${Flex} {
    flex-direction: ${p => (p.stacked ? 'column' : 'row')};
  }
  ${Button} {
    ${p =>
      p.stacked &&
      `margin-left: 0;
    margin-top: ${layout.measurements.spacing050};`}
  }
`;

export const NewsLetterFooterText = styled.div`
  > * {
    margin: 0;
  }
  ${media.mediumDown`
    flex-grow: 1;
    max-width: 260px;
  `}
`;
export const NewsLetterFooterInput = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 370px;
  flex-grow: 1;

  form {
    margin: 0;
  }

  ${Button} {
    width: unset;

    ${media.mediumDown`
      width: 100%;
      margin-left: 0;
      margin-top: ${layout.measurements.spacing100};
    `}
  }

  > form > ${Flex} {
    ${media.mediumDown`
      flex-direction: column;
    `}
  }
`;

export const ButtonFooter = styled(PrimaryButton)`
  margin-left: ${layout.measurements.spacing050};
  height: min-content;
`;

export const NewsLetterFooter = styled.div<NewsLetterProps>`
  display: flex;
  flex-direction: column;
  gap: ${layout.measurements.spacing100};
  margin: 0;
  color: ${p => p.variant === 'secondary' && p.theme.colors.base600};
  ${Title} {
    color: ${p => p.variant === 'secondary' && p.theme.colors.base700};
  }
  ${Legal} {
    a {
      color: ${p =>
        p.variant === 'primary'
          ? p.theme.colors.base600
          : p.theme.colors.base600};

      :hover {
        color: ${p =>
          p.variant === 'primary'
            ? p.theme.colors.base600
            : p.theme.text.callToActionHover};
      }
    }

    ${media.mediumDown`
      text-align: left;
    `}
  }
  ${Feedback} {
    color: ${p =>
      p.variant === 'primary'
        ? p.theme.colors.negative700
        : p.theme.colors.base600};
  }
  ${Flex} {
    flex-wrap: nowrap;
    flex-direction: ${p => (p.stacked ? 'column' : 'row')};
  }
  ${Button} {
    ${p =>
      p.stacked &&
      `margin-left: 0;
    margin-top: ${layout.measurements.spacing050};`}
  }

  ${media.mediumDown`
    align-items: center;
    gap: ${layout.measurements.spacing150};
  `}
`;

export const TextFooter = styled(BodyMediumText)`
  margin-bottom: ${layout.measurements.spacing100};
`;
